import styles from "./style.module.scss";
import "leaflet/dist/leaflet.css";
import { h } from "preact";
import { useEffect, useState, useRef } from "preact/hooks";
import { Text } from "preact-i18n";
import { MapContainer, TileLayer, Circle, Popup } from "react-leaflet";
import L, { LatLng } from "leaflet";

import markerIcon2x from "../../images/marker-blue-2x.png";
import markerIcon from "../../images/marker-blue.png";
import markerIconSelected2x from "../../images/marker-orange-2x.png";
import markerIconSelected from "../../images/marker-orange.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";
import LoadingIcon from "icons/LoadingIcon";

import MvAdressResult from "models/MvAddressResult";
import MvArea from "models/MvArea";
import { CountryCode } from "models/CountryCode";

import { getLocations } from "services/api";

// @ts-expect-error Leaflet's private method is absent from its public types.
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconUrl: markerIcon,
    iconSize: [25, 34],
    iconRetinaUrl: markerIcon2x,
    shadowUrl: markerShadow,
    shadowAnchor: [13, 46],
});

import CenterMap from "./CenterMap";
import CustomMarker from "./CustomMarker";
import DynamicMarkers from "./DynamicMarkers";
import { useLatestRequest } from "services/useLatestRequest";
import type { LoadingActions } from "components/StoreContextWrapper/actions";
export type { CenterMapProps } from "./CenterMap";

export interface MapProps extends Partial<LoadingActions> {
    areas: MvArea[];
    apiBasePath?: string;
    onSelect(area: MvArea): void;
    addAddress(address: MvAdressResult): void;
    updateAddress(area: MvArea, address: MvAdressResult): void;
    countryCode?: CountryCode;
    isLoading: boolean;
}
const Map = ({
    areas,
    onSelect,
    apiBasePath,
    addAddress,
    updateAddress,
    countryCode,
    isLoading: isLoadingNow,
    startLoading,
    stopLoading,
}: MapProps) => {
    const [isAdding, setIsAdding] = useState(false);
    const [notFound, setNotFound] = useState(false);
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const mapMarkers = areas.filter((area) => area.result != null);
    useEffect(
        () => () => {
            if (timerRef.current) clearTimeout(timerRef.current);
        },
        [],
    );
    const showNotfound = () => {
        if (timerRef.current) clearTimeout(timerRef.current);
        setNotFound(true);
        timerRef.current = setTimeout(() => setNotFound(false), 2000);
    };
    const mapRequest = useLatestRequest<{
        addresses: MvAdressResult[];
        currentArea?: MvArea;
    }>({
        startLoading,
        stopLoading,
        onResult: ({ addresses, currentArea }) => {
            if (!addresses.length) return showNotfound();
            setIsAdding(false);
            if (currentArea) updateAddress(currentArea, addresses[0]);
            else addAddress(addresses[0]);
        },
        onError: () => showNotfound(),
    });
    const handleAddressSelected = (latlng: LatLng, currentArea?: MvArea) => {
        void mapRequest.run(async (signal) => ({
            addresses: (
                await getLocations(
                    apiBasePath || "localhost:3000",
                    {
                        c: "objects",
                        latitude: latlng.lat,
                        longitude: latlng.lng,
                        distance: 0.05,
                        countryCode: countryCode || "nl",
                    },
                    signal,
                )
            ).data,
            currentArea,
        }));
    };
    return (
        <div className={styles.mvMapWrapper}>
            <MapContainer
                style={{
                    height: "100%",
                    width: "100%",
                }}
                center={[52.370216, 4.895168]}
                zoom={7}
                preferCanvas={true}
            >
                <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                {mapMarkers.map((a: MvArea) => (
                    <CustomMarker
                        key={a.id}
                        area={a}
                        onPositionUpdate={handleAddressSelected}
                        position={[
                            a.result?.breedtegraad ?? 0,
                            a.result?.lengtegraad ?? 0,
                        ]}
                        icon={
                            a.isSelected
                                ? new L.Icon.Default({
                                      iconUrl: markerIconSelected,
                                      iconRetinaUrl: markerIconSelected2x,
                                      className: !a.disabled ? "icon-drag" : "",
                                  })
                                : new L.Icon.Default({
                                      className: !a.disabled ? "icon-drag" : "",
                                  })
                        }
                        onSelect={onSelect}
                        draggable={!a.disabled}
                    >
                        {!isAdding && (
                            <Popup>
                                <strong>
                                    {a.result?.straatnaam} {a.result?.huisnr}
                                    {a.result?.huisnr_bag_letter
                                        ? `${a.result?.huisnr_bag_letter}${
                                              a.result?.huisnr_bag_toevoeging ||
                                              ""
                                          }`
                                        : ""}
                                </strong>
                                <br />
                                {a.result?.postcode}, {a.result?.plaatsnaam}
                            </Popup>
                        )}
                        <Circle
                            radius={a.inputParams.radius}
                            center={[
                                a.result?.breedtegraad ?? 0,
                                a.result?.lengtegraad ?? 0,
                            ]}
                            disabled={true}
                            pathOptions={{
                                color: a.isSelected ? "#F28E3D" : "#14456B",
                            }}
                        />
                    </CustomMarker>
                ))}

                <CenterMap areas={areas} />
                <DynamicMarkers
                    disabled={isLoadingNow}
                    isAdding={isAdding}
                    onIsAdding={(val: boolean) => setIsAdding(val)}
                    onAddressSelected={(latLng: LatLng) =>
                        handleAddressSelected(latLng)
                    }
                />
                <div
                    className={`${styles.loadingOverlay} ${
                        mapRequest.isPending ? styles.loadingShow : null
                    }`}
                >
                    <LoadingIcon
                        fill="#14456b"
                        className={styles.loadingOverlayIcon}
                    />
                </div>
                <div
                    className={`${styles.addMarkerMessage} ${
                        notFound ? styles.showMessage : null
                    }`}
                >
                    <Text id="general.messages.addressNotFound" plural={1}>
                        No address found
                    </Text>
                </div>
            </MapContainer>
        </div>
    );
};
export default Map;
